import React, { useState } from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import useHomeContext from 'Context/HomeContext';
import useMountedGuard from 'Hooks/useMountedGuard';

const AnnouncementContext = React.createContext(undefined);

// Actions
const INITIALIZE_STARTED = 'initialize-started';
const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
const INITIALIZE_FAILED = 'initialize-failed';

const CLEAR_BANNER_ANNOUNCEMENT = 'clear-banner-announcement';
const CLEAR_MODAL_ANNOUNCEMENT = 'clear-modal-announcement';

const NEXT_STEP = 'next-step';
const PREV_STEP = 'prev-step';

const init = () => {
  return {
    modalNumSteps: null,
    modalStepIdx: null,
    isInitializing: false,
    isInitialized: false,
    initializingError: null,
    bannerAnnouncementCollection: null,
    bannerAnnouncementResource: null,
    modalAnnouncementCollection: null,
    modalAnnouncementResource: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        isInitialized: true,
        initializingError: null,
        modalStepIdx: 0,
        modalNumSteps: action.modalAnnouncementCollection?.resources()?.head()?.attr('steps')?.resources()
          ?.length() || 0,
        bannerAnnouncementResource: action.bannerAnnouncementCollection?.resources().head(),
        bannerAnnouncementCollection: action.bannerAnnouncementCollection,
        modalAnnouncementResource: action.modalAnnouncementCollection?.resources()?.head(),
        modalAnnouncementCollection: action.modalAnnouncementCollection,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case CLEAR_MODAL_ANNOUNCEMENT: {
      return {
        ...state,
        modalStepIdx: null,
        modalNumSteps: null,
        modalAnnouncementResource: null,
        modalAnnouncementCollection: null,
      };
    }
    case CLEAR_BANNER_ANNOUNCEMENT: {
      return {
        ...state,
        bannerAnnouncementResource: null,
        bannerAnnouncementCollection: null,
      };
    }
    case PREV_STEP: {
      return {
        ...state,
        modalStepIdx: Math.max(0, state.modalStepIdx - 1),
      };
    }
    case NEXT_STEP: {
      return {
        ...state,
        modalStepIdx: Math.min(state.modalStepIdx + 1, state.modalNumSteps - 1), // Max value is the length of the list
      };
    }
    default: {
      throw new Error(`Unhandled AnnouncementContext action type: ${action.type}`);
    }
  }
};

const AnnouncementProvider = ({
  children,
}) => {
  const { refreshHomeResource } = useHomeContext();
  const [bannerCanBeShown, setBannerCanBeShown] = useState(false);

  const [state, _dispatch] = React.useReducer(reducer, null, init);
  const { doWithMountedGuard } = useMountedGuard();
  const dispatch = doWithMountedGuard(_dispatch);

  const goToPreviousStep = () => {
    dispatch({ type: PREV_STEP });
  };

  const goToNextStep = React.useCallback(() => {
    dispatch({ type: NEXT_STEP });
  }, [state]);

  const clearModalAnnouncement = React.useCallback(() => dispatch({ type: CLEAR_MODAL_ANNOUNCEMENT }), [dispatch]);

  const markBannerAsDismissed = () => {
    state.bannerAnnouncementResource.link('self').copy()
      .withQueryParams().equal('action', 'dismissed')
      .put();
    dispatch({ type: CLEAR_BANNER_ANNOUNCEMENT });
  };

  const markModalAsDismissed = () => {
    state.modalAnnouncementResource.link('self').copy()
      .withQueryParams().equal('action', 'dismissed')
      .put();
    dispatch({ type: CLEAR_MODAL_ANNOUNCEMENT });
  };

  const markBannerAsSeen = React.useCallback(() => {
    state.bannerAnnouncementResource.link('self').copy()
      .withQueryParams().equal('action', 'seen')
      .put();
  }, [state]);

  const markModalAsSeen = React.useCallback(() => {
    state.modalAnnouncementResource.link('self').copy()
      .withQueryParams().equal('action', 'seen')
      .put();
  }, [state]);

  const loadAnnouncementCollection = async () => {
    // We must refresh home so that we get an updated announcement relationship on the home resource to
    // ensure that we do not show users a stale announcement that they have already completed.
    const homeRes = await refreshHomeResource();
    const relationships = [
      homeRes.relationship(fr.relationships.bannerAnnouncement.name)?.getRelated(),
      homeRes.relationship(fr.relationships.modalAnnouncement.name)?.getRelated(),
    ];

    // If there's no relationships to get, then we can move on
    if (relationships.every(rel => !rel)) {
      return null;
    }

    try {
      dispatch({ type: INITIALIZE_STARTED });

      const [banner, modal] = await Promise.all(relationships);

      // If we didn't get an announcement back, just chill, this was probably a redirect and we don't want to unplug. Better to not get an announcement than to unplug.
      if ((banner && banner.resources()?.head()?.type() !== 'Announcement') || (modal && modal.resources()?.head()?.type() !== 'Announcement')) {
        return null;
      }

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        modalAnnouncementCollection: modal,
        bannerAnnouncementCollection: banner,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_FAILED, initializationError: err });
    }

    return null;
  };

  React.useEffect(() => {
    if (!state.isInitialized) {
      loadAnnouncementCollection();
    }
  }, [state.isInitialized]);

  const memoizedValue = React.useMemo(() => {
    const currentModalStepResource = (state.modalAnnouncementResource) ? state.modalAnnouncementResource.attr('steps').resources().index(state.modalStepIdx) : null;

    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      modal: {
        isDialogOpen: !!currentModalStepResource,
        announcementCollection: state.modalAnnouncementCollection,
        announcementResource: state.modalAnnouncementResource,
        currentStepResource: currentModalStepResource,
        isFirstStep: state.modalStepIdx === 0,
        isLastStep: state.modalStepIdx === state.modalNumSteps - 1,
        currentStepIdx: state.modalStepIdx,
        totalSteps: state.modalNumSteps,
        goToNextStep,
        goToPreviousStep,
        clearAnnouncement: clearModalAnnouncement,
        markAsDismissed: markModalAsDismissed,
        markAsSeen: markModalAsSeen,
        isDismissible: state.modalAnnouncementResource?.attr('isDismissible') === 'true',
      },
      banner: {
        resource: state.bannerAnnouncementResource,
        canBeShown: bannerCanBeShown,
        setCanBeShown: setBannerCanBeShown,
        initializing: state.isInitializing,
        markAsSeen: markBannerAsSeen,
        markAsDismissed: markBannerAsDismissed,
        isDismissible: state.bannerAnnouncementResource?.attr('isDismissible') === 'true',
      },
    };
  }, [
    state,
    goToNextStep,
    goToPreviousStep,
    markBannerAsDismissed,
    markModalAsDismissed,
    markBannerAsSeen,
    markModalAsSeen,
    clearModalAnnouncement,
  ]);

  return (
    <AnnouncementContext.Provider value={memoizedValue}>
      {children}
    </AnnouncementContext.Provider>
  );
};

// Use Announcement State Context
const useAnnouncementContext = () => {
  const context = React.useContext(AnnouncementContext);
  if (context === undefined) {
    throw new Error('useAnnouncementContext must be used within a AnnouncementProvider');
  }
  return context;
};

AnnouncementProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  AnnouncementProvider,
  useAnnouncementContext,
};
